/**
 * FERP Portal - Universal Module Browser Page
 *
 * Replaces per-subgraph hand-coded pages (HealthcarePage, AccountingPage, etc.)
 * with a single data-driven browser that works for ANY FERP Solution (module).
 *
 * User-facing terminology: "Solution" (not "subgraph" — technical jargon)
 * Internal/code: still uses "subgraph" for consistency with backend
 *
 * Features:
 * - Overview dashboard as default first tab
 * - Module header with icon, compliance badges, life-critical warning
 * - Adaptive entity type picker (tabs ≤12, searchable sidebar >12)
 * - Tabulator-powered entity data grid with search, sort, export
 * - Deep-link support via ?entity=Patient URL param
 *
 * FERP Principles:
 * - Data-driven: All config from SubgraphMetadata
 * - Repository pattern: Data via useSubgraphEntityData hook
 * - Dumb frontend: Only renders what metadata defines
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Alert,
  Loader,
  Center,
  ThemeIcon,
  Flex,
  Button,
} from '@mantine/core';
import { IconShieldCheck, IconAlertTriangle, IconPuzzle } from '@tabler/icons-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTenantId, useViewLayer } from '@/auth';
import { useTenantSubgraphConfig, type SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import { useEntityAccessTracker } from '@/hooks/useEntityAccessTracker';
import { useSmartEntityTypes } from '@/hooks/useSmartEntityTypes';
import { useGetFormSchemaOverridesQuery } from '@/generated/graphql';
import { useNavigation } from '@/contexts/NavigationContext';
import { getIcon } from '@/utils/iconMap';
import { useLensEvaluation, useAssignedEntityIds } from '@ferp/workflow-forms';
import { useFhirLabels } from '@/hooks/useFhirLabels';
import { EntityTypePicker } from './components/EntityTypePicker';
import { SubgraphEntityBrowser } from './components/SubgraphEntityBrowser';
import { ModuleOverview } from './components/ModuleOverview';
import { PatientContextSelector } from './components/PatientContextSelector';

/**
 * Known subgraph ID prefixes for URL pattern matching.
 * These match SUBGRAPH_DEFINITIONS keys in useTenantSubgraphConfig.
 * Used to detect subgraph-prefixed URLs like /fhir/patients, /payroll/time-entries.
 */
const KNOWN_SUBGRAPH_PREFIXES = new Set([
  'fhir', 'payroll', 'accounting', 'edi', 'website',
  'workforce', 'legal', 'manufacturing', 'government', 'wearables',
  'erp', 'core',
  // 'ai', 'portal' - disabled until subgraph configs exist
]);

/**
 * Extract subgraphId from the current URL path.
 * Supports multiple patterns:
 *   /admin/{subgraphId}          → subgraphId (scope-rewritten paths)
 *   /architect/{subgraphId}      → subgraphId (scope-rewritten paths)
 *   /{subgraphId}/{entitySlug}   → subgraphId (old-style config routes)
 *   /{subgraphId}/admin/{slug}   → subgraphId (subgraph-scoped admin)
 */
function useSubgraphIdFromPath(): string | null {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  // Pattern 1: /admin/{subgraphId} or /architect/{subgraphId}
  if (segments.length >= 2 && (segments[0] === 'admin' || segments[0] === 'architect')) {
    return segments[1] ?? null;
  }

  // Pattern 2: /{subgraphId}/... (subgraph-prefixed config routes)
  if (segments.length >= 1 && KNOWN_SUBGRAPH_PREFIXES.has(segments[0]!)) {
    return segments[0] ?? null;
  }

  return null;
}

export function SubgraphBrowserPage(): React.ReactElement {
  const subgraphId = useSubgraphIdFromPath();
  const tenantId = useTenantId();
  const viewLayer = useViewLayer();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    loading: configLoading,
    error: configError,
    getSubgraphConfig,
  } = useTenantSubgraphConfig(tenantId);

  // Get subgraph configuration from backend metadata
  const subgraphConfig = useMemo(() => {
    if (!subgraphId) return null;
    return getSubgraphConfig(subgraphId);
  }, [subgraphId, getSubgraphConfig]);

  // DRR-103/313: Scope-filtered entity types from shared NavigationContext.
  const { allowedNavigation, loading: navLoading } = useNavigation();

  // DRR-207: Get FormSchemaOverride summaries for this subgraph.
  // Used for "X overrides" badge on FHIR entity cards (jurisdiction-specific fields).
  const { data: fsoData } = useGetFormSchemaOverridesQuery({
    variables: { subgraph: subgraphId ?? '' },
    skip: !subgraphId || subgraphId !== 'fhir', // Only FHIR has jurisdiction overrides
  });

  // Build lookup maps: entityType → override count, entityType → mandatory info
  const { overrideCountMap, mandatoryMap } = useMemo(() => {
    const countMap = new Map<string, number>();
    const mandMap = new Map<string, { mandatory: boolean; statuteRef?: string }>();
    if (!fsoData?.formSchemaOverrides) return { overrideCountMap: countMap, mandatoryMap: mandMap };
    for (const fso of fsoData.formSchemaOverrides) {
      const total =
        fso.hiddenFieldCount +
        fso.readonlyFieldCount +
        fso.customFieldCount +
        fso.labelOverrideCount;
      countMap.set(fso.entityType, total);
      // DRR-207: Track mandatory status for compliance badges
      if (fso.mandatory) {
        mandMap.set(fso.entityType, { mandatory: true, statuteRef: fso.statuteRef ?? undefined });
      }
    }
    return { overrideCountMap: countMap, mandatoryMap: mandMap };
  }, [fsoData]);

  // Selected entity type: null = Overview dashboard, SubgraphEntityType = entity browser
  const [selectedType, setSelectedType] = useState<SubgraphEntityType | null>(null);

  // DRR-103 Phase 2: Lens evaluation for field-level filtering (hiddenFields)
  const { evaluateLens, evaluation } = useLensEvaluation();
  const currentActorId = useMemo(() => {
    try { return localStorage.getItem('dev_actor_auth_id') ?? 'anonymous'; }
    catch { return 'anonymous'; }
  }, []);

  // Evaluate lens when entity type changes — provides hiddenFields for column filtering
  useEffect(() => {
    if (selectedType && currentActorId) {
      evaluateLens({ actorId: currentActorId });
    }
  }, [selectedType, currentActorId, evaluateLens]);

  const hiddenFields = useMemo(
    () => evaluation?.hiddenFields ?? [],
    [evaluation],
  );

  // DRR-103 Phase 2: Scope-based row filtering via ASSIGNED_TO + CORRELATES edges.
  // Only active in 'user' view — admin/architect see all entities.
  const { correlatedEntityIds } = useAssignedEntityIds({
    actorId: currentActorId,
    skip: viewLayer !== 'user',
  });

  // FHIR terminology: backend-driven via Setting config fhir_labels + ln-5-2-001 lens.
  // Applied here so all entity type cards use ALF terms (Resident, Service Visit, etc.)
  const { getFhirLabel } = useFhirLabels();

  // DRR-123: Scope-filtered entity types — RESTRICTIVE default.
  // If allowedNavigation hasn't loaded yet, show NOTHING (not everything).
  // Wall enforcement: DON (clinical wall) must never see Claims.
  const entityTypes = useMemo(() => {
    const allEntityTypes = subgraphConfig?.entityTypes ?? [];
    const allowedTypes = allowedNavigation?.allowedEntityTypes;

    // RESTRICTIVE: If scope query hasn't returned yet, show nothing.
    // This prevents flashing all entity tabs before filtering kicks in.
    if (!allowedTypes) {
      return [];
    }

    // If backend returned an empty list, user has no entity access — show nothing.
    if (allowedTypes.length === 0) {
      return [];
    }

    // Build sets for flexible matching — handle graphqlType format mismatch
    // (e.g., backend may return 'Patient' vs frontend fallback 'FhirPatient')
    const allowedTypeNames = new Set(allowedTypes.map(et => et.graphqlType.toLowerCase()));
    const allowedNames = new Set(allowedTypes.map(et => et.name.toLowerCase()));

    // Filter this subgraph's entity types to only those the user can see.
    // Dedup by graphqlType — seed data may contain duplicate entries (e.g., categorized + bare).
    // Match on graphqlType OR name (case-insensitive) to handle format mismatches.
    const seen = new Set<string>();
    const filtered = allEntityTypes.filter(et => {
      const typeMatch = allowedTypeNames.has(et.graphqlType.toLowerCase()) ||
        allowedTypeNames.has(et.graphqlType.replace(/^Fhir/, '').toLowerCase()) ||
        allowedNames.has(et.name.toLowerCase());
      if (!typeMatch) return false;
      if (seen.has(et.graphqlType)) return false;
      seen.add(et.graphqlType);
      return true;
    });

    // Apply FHIR terminology translation for healthcare subgraph (Setting config fhir_labels).
    // Backend entity types use generic FHIR R4 names; getFhirLabel maps them to ALF terms.
    if (subgraphId !== 'fhir') return filtered;
    return filtered.map(et => {
      const translatedName = getFhirLabel(et.name);
      if (translatedName === et.name) return et; // no mapping — unchanged
      const pluralName = translatedName.endsWith('y')
        ? translatedName.slice(0, -1) + 'ies'
        : translatedName.endsWith('s')
          ? translatedName + 'es'
          : translatedName + 's';
      return { ...et, name: translatedName, pluralName };
    });
  }, [subgraphConfig, allowedNavigation, subgraphId, getFhirLabel]);

  // Smart Navigation: track access frequency and reorder entity types
  const tracker = useEntityAccessTracker();
  const { orderedEntityTypes, quickAccessCount, isPersonalized } =
    useSmartEntityTypes(subgraphId ?? '', entityTypes);

  // DRR-207: Get FSO override count from backend query.
  // Only show for FHIR subgraph — other subgraphs don't have jurisdiction overrides.
  // NOTE: Must be defined BEFORE any conditional returns (React hooks order rule).
  const getOverrideCount = useCallback(
    (entityType: string): number => {
      if (subgraphId !== 'fhir') return 0;
      return overrideCountMap.get(entityType) ?? 0;
    },
    [subgraphId, overrideCountMap]
  );

  // DRR-207: Get mandatory compliance info for entity type.
  // Returns undefined if not mandatory, or { mandatory: true, statuteRef } if required.
  const getMandatoryInfo = useCallback(
    (entityType: string): { mandatory: boolean; statuteRef?: string } | undefined => {
      if (subgraphId !== 'fhir') return undefined;
      return mandatoryMap.get(entityType);
    },
    [subgraphId, mandatoryMap]
  );

  // Initialize from URL param — default to Overview (null)
  useEffect(() => {
    const entityParam = searchParams.get('entity');
    if (entityParam && orderedEntityTypes.length > 0) {
      const found = orderedEntityTypes.find((et) => et.graphqlType === entityParam);
      if (found) {
        setSelectedType(found);
        return;
      }
    }
    // Default to Overview
    setSelectedType(null);
  }, [orderedEntityTypes, searchParams]);

  // Handle entity type selection — update URL param only (useEffect derives selectedType)
  const handleSelectType = useCallback(
    (entityType: SubgraphEntityType | null) => {
      if (entityType) {
        setSearchParams({ entity: entityType.graphqlType }, { replace: true });
        // Smart Navigation: record access for personalization
        if (subgraphId) {
          tracker.recordAccess(subgraphId, entityType.graphqlType);
        }
      } else {
        // Overview — clear entity param
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams, tracker, subgraphId],
  );

  // Loading state
  // DRR-123: Restrictive default — block rendering until BOTH config AND scopes are resolved.
  // Prevents flashing all entity tabs before scope filtering kicks in.
  if (configLoading || navLoading) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Stack ta="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading solution configuration...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state
  if (configError) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Configuration Error" icon={<IconAlertTriangle size={16} />}>
          Failed to load solution configuration: {configError.message}
        </Alert>
      </Container>
    );
  }

  // No subgraphId in URL
  if (!subgraphId) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" title="No Solution Selected" icon={<IconAlertTriangle size={16} />}>
          Unable to determine which solution to display. Please navigate using the sidebar.
        </Alert>
      </Container>
    );
  }

  // Solution not enabled for tenant
  if (!subgraphConfig) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" title="Solution Not Available" icon={<IconAlertTriangle size={16} />}>
          This solution is not enabled for your tenant.
          Contact your administrator to enable it.
        </Alert>
      </Container>
    );
  }

  // No visible entity types — either not configured or scope-restricted
  const hasAllTypes = (subgraphConfig?.entityTypes ?? []).length > 0;
  const userScopes = allowedNavigation?.userScopes ?? [];
  const allowedEntityTypeCount = allowedNavigation?.allowedEntityTypes?.length ?? 0;

  if (entityTypes.length === 0) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <ModuleHeader config={subgraphConfig} />
          <Alert
            color={hasAllTypes ? 'yellow' : 'blue'}
            title={hasAllTypes ? 'Access Restricted' : 'No Data Types Configured'}
            icon={hasAllTypes ? <IconShieldCheck size={16} /> : undefined}
          >
            {hasAllTypes
              ? 'Your current role does not have access to any data types in this solution. Contact your administrator for access.'
              : 'This solution has no data types configured yet.'}
          </Alert>
          {/* DEBUG: Show scope resolution info for troubleshooting */}
          {process.env.NODE_ENV !== 'production' || userScopes.length === 0 ? (
            <Alert color="gray" title="Debug Info" style={{ maxHeight: 300, overflow: 'auto' }}>
              <Group justify="flex-end" mb="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    const debugData = {
                      viewLayer,
                      userScopes,
                      allowedEntityTypes: allowedEntityTypeCount,
                      subgraphEntityTypes: subgraphConfig?.entityTypes?.length ?? 0,
                      subgraphId,
                      subgraphConfig: subgraphConfig ? {
                        displayName: subgraphConfig.displayName,
                        entityTypes: subgraphConfig.entityTypes,
                        status: subgraphConfig.status,
                      } : null,
                      allowedNavData: allowedNavigation ?? null,
                    };
                    navigator.clipboard.writeText(JSON.stringify(debugData, null, 2));
                  }}
                >
                  Copy as JSON
                </Button>
              </Group>
              <Text size="xs" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                viewLayer: {viewLayer}{'\n'}
                userScopes ({userScopes.length}): {JSON.stringify(userScopes, null, 2)}{'\n'}
                allowedEntityTypes: {allowedEntityTypeCount}{'\n'}
                subgraphEntityTypes: {subgraphConfig?.entityTypes?.length ?? 0}{'\n'}
                subgraphId: {subgraphId}
              </Text>
            </Alert>
          ) : null}
        </Stack>
      </Container>
    );
  }

  // Determine layout: for sidebar mode (>12 types), use side-by-side layout
  const useSidebar = orderedEntityTypes.length > 12;

  // Content: Overview dashboard or entity browser
  const content = selectedType ? (
    <SubgraphEntityBrowser
      subgraphId={subgraphId}
      entityType={selectedType}
      hiddenFields={hiddenFields}
      allowedEntityIds={correlatedEntityIds}
    />
  ) : (
    <ModuleOverview
      displayName={subgraphConfig.displayName}
      description={subgraphConfig.description}
      color={subgraphConfig.color}
      isLifeCritical={subgraphConfig.isLifeCritical}
      complianceFrameworks={subgraphConfig.complianceFrameworks}
      entityTypes={orderedEntityTypes}
      onEntityClick={handleSelectType}
      quickAccessCount={quickAccessCount}
      isPersonalized={isPersonalized}
      getOverrideCount={getOverrideCount}
      getMandatoryInfo={getMandatoryInfo}
      subgraphId={subgraphId}
    />
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <ModuleHeader config={subgraphConfig} />

        {/* DRR-318: Patient context selector for FHIR subgraph */}
        <PatientContextSelector subgraphId={subgraphId} />

        {useSidebar ? (
          <Flex gap="md" ta="left">
            <EntityTypePicker
              entityTypes={orderedEntityTypes}
              selectedType={selectedType}
              onSelect={handleSelectType}
              quickAccessCount={quickAccessCount}
              isPersonalized={isPersonalized}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {content}
            </div>
          </Flex>
        ) : (
          <Stack gap="md">
            <EntityTypePicker
              entityTypes={orderedEntityTypes}
              selectedType={selectedType}
              onSelect={handleSelectType}
              quickAccessCount={quickAccessCount}
              isPersonalized={isPersonalized}
            />
            {content}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

/**
 * Module header with icon, name, description, "FERP Solution" badge, compliance badges.
 */
function ModuleHeader({ config }: Readonly<{ config: { displayName: string; description: string; icon: string; color: string; isLifeCritical: boolean; complianceFrameworks: string[] } }>) {
  const Icon = getIcon(config.icon);

  return (
    <Group justify="space-between" ta="left">
      <Group gap="sm">
        <ThemeIcon size="lg" variant="light" color={config.color}>
          <Icon size={20} />
        </ThemeIcon>
        <div>
          <Group gap="xs">
            <Title order={2}>{config.displayName}</Title>
            <Badge
              variant="light"
              color="gray"
              size="sm"
              leftSection={<IconPuzzle size={10} />}
            >
              FERP Solution
            </Badge>
          </Group>
          <Text c="dimmed" size="sm">{config.description}</Text>
        </div>
      </Group>
      <Group gap="xs">
        {config.isLifeCritical && (
          <Badge
            color="red"
            variant="filled"
            leftSection={<IconShieldCheck size={12} />}
          >
            Life-Critical
          </Badge>
        )}
        {config.complianceFrameworks.map((fw) => (
          <Badge key={fw} variant="outline" color="gray" size="sm">{fw}</Badge>
        ))}
      </Group>
    </Group>
  );
}

export default SubgraphBrowserPage;
